import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { clearAuth, setTokens, syncAuthFromPersistSnapshot } from '@store/slices/authSlice';
import type { AuthTokens, User } from '@appTypes/auth.types';

const SESSION_KEY = 'persist:pr-gen-session';

interface PersistedAuthShape {
  user?: User | null;
  tokens?: AuthTokens | null;
  isAuthenticated?: boolean;
}

function parsePersistedAuth(raw: string | null): PersistedAuthShape | null {
  if (!raw) return null;
  try {
    const persisted = JSON.parse(raw) as Record<string, string>;
    if (typeof persisted.auth !== 'string') return null;
    return JSON.parse(persisted.auth) as PersistedAuthShape;
  } catch {
    return null;
  }
}

/**
 * Cross-tab session sync. The browser fires the ``storage`` event on
 * every tab *except* the one that performed the write — so when one tab
 * refreshes its token via ``refreshSessionAsync`` (or logs out), other
 * tabs pick up the new ``expiresAt`` here and mirror it into their own
 * Redux state. The session bar in those tabs jumps back to green
 * automatically because it reads ``auth.tokens.expiresAt``.
 *
 * When a sibling tab logs in while this tab is still on ``/login``, we must
 * mirror **user + tokens + isAuthenticated** together. Applying only
 * ``setTokens`` leaves ``isAuthenticated: false``; the next persist from this
 * tab looks like a logout (``isAuthenticated: false`` but tokens still present)
 * and would incorrectly ``clearAuth`` in the tab that just signed in.
 */
export function SessionSyncListener() {
  const dispatch = useAppDispatch();
  const currentExpiresAt = useAppSelector((s) => s.auth.tokens?.expiresAt ?? null);
  const currentIsAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== SESSION_KEY) return;
      const next = parsePersistedAuth(event.newValue);
      if (!next) return;

      const nextTokens = next.tokens ?? null;

      // Sibling tab finished login / bootstrap — copy the full snapshot (Bearer or cookie-only).
      if (!currentIsAuthenticated && next.isAuthenticated === true && next.user) {
        dispatch(
          syncAuthFromPersistSnapshot({
            user: next.user,
            tokens: nextTokens,
            isAuthenticated: true,
          }),
        );
        return;
      }

      // Real logout: other tab cleared the session. Ignore ``isAuthenticated: false`` when
      // access tokens are still present — that is the broken partial state from the old
      // ``setTokens``-only path, not an intentional sign-out.
      if (currentIsAuthenticated && next.isAuthenticated === false && !nextTokens?.accessToken) {
        dispatch(clearAuth());
        return;
      }

      // Cookie / httpOnly-JWT sessions persist ``tokens: null`` while still logged in.
      // Token rotation for Bearer sessions while both tabs are already signed in.
      if (nextTokens && nextTokens.expiresAt !== currentExpiresAt) {
        dispatch(setTokens(nextTokens));
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [dispatch, currentExpiresAt, currentIsAuthenticated]);

  return null;
}
